import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Row, Col, Tag, Button, Descriptions, Space, Divider, Spin, Popconfirm, Alert,
} from 'antd';
import {
  ArrowLeftOutlined, FilePdfOutlined, SendOutlined, StopOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import {
  getAttestation, revoquer, ouvrirPdf,
  STATUTS_ATTESTATION_DIGITALE, STATUTS_INSTANCE,
} from '../../services/attestationDigitaleService';
import RenvoyerAttestationModal from './RenvoyerAttestationModal';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';

export default function DetailAttestationDigitale() {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('attestationsDigitales');

  const [att, setAtt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renvoyerModal, setRenvoyerModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAttestation(trackingId);
      setAtt(data);
    } catch {
      toast.error(i18n.t('attestationsDigitales:detail.introuvable'));
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  useEffect(() => { load(); }, [load]);

  const handleRevoquer = async () => {
    try {
      await revoquer(trackingId, { motif: 'Révoqué par BNCB' });
      toast.success(t('messages.revoquee'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('messages.erreurRevocation'));
    }
  };

  const handlePdf = async () => {
    try {
      await ouvrirPdf(att.digitale.tokenTelechargement);
    } catch {
      toast.error(t('messages.erreurPdf'));
    }
  };

  if (loading) return <Layout title={t('detail.titreLayout')}><Spin style={{ margin: 40 }} /></Layout>;
  if (!att) return <Layout title={t('detail.titreLayout')}><Alert type="error" message={t('detail.introuvable')} /></Layout>;

  const statutInfo = STATUTS_ATTESTATION_DIGITALE[att.statut] || { label: att.statut, color: 'default' };
  const digitale = att.digitale;

  return (
    <Layout title={t('detail.titre', { numero: att.numeroAttestation })}>
      <div className="cb-page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/attestations-digitales')}>
            {t('detail.retour')}
          </Button>
          <h1 className="cb-page-title" style={{ margin: 0 }}>
            {att.numeroAttestation}
            <Tag color={statutInfo.color} style={{ marginLeft: 12, fontSize: 13 }}>
              {statutInfo.label}
            </Tag>
          </h1>
        </Space>
        <Space>
          {digitale?.tokenTelechargement && (
            <Button icon={<FilePdfOutlined />} onClick={handlePdf}>
              {t('detail.voirPdf')}
            </Button>
          )}
          {att.statut === 'ACTIVE' && (
            <>
              <Button icon={<SendOutlined />} onClick={() => setRenvoyerModal(true)}>
                {t('actions.renvoyer')}
              </Button>
              <Popconfirm
                title={t('detail.revoquerTitre')}
                onConfirm={handleRevoquer}
                okText={t('actions.revoquer')} cancelText={t('actions.annuler')} okButtonProps={{ danger: true }}>
                <Button danger icon={<StopOutlined />}>{t('actions.revoquer')}</Button>
              </Popconfirm>
            </>
          )}
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <div className="cb-card" style={{ padding: 20 }}>
            <Divider orientation="left" style={{ fontSize: 13, marginTop: 0 }}>{t('detail.souscripteur')}</Divider>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('detail.nomComplet')}>
                {att.souscripteurNom} {att.souscripteurPrenom || ''}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.telephone')}>{att.souscripteurTelephone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email">{att.souscripteurEmail || '-'}</Descriptions.Item>
            </Descriptions>

            <Divider orientation="left" style={{ fontSize: 13 }}>{t('detail.vehicule')}</Divider>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('detail.immatriculation')}>
                <strong>{att.vehiculeImmatriculation}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.marqueModele')}>
                {att.vehiculeMarque || '-'} {att.vehiculeModele || ''}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.chassis')}>{att.vehiculeChassis || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail.genre')}>{att.vehiculeGenre || '-'}</Descriptions.Item>
            </Descriptions>
          </div>
        </Col>

        <Col xs={24} lg={12}>
          <div className="cb-card" style={{ padding: 20 }}>
            <Divider orientation="left" style={{ fontSize: 13, marginTop: 0 }}>{t('detail.policeCouverture')}</Divider>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('detail.numeroPolice')}>{att.numeroPolice}</Descriptions.Item>
              <Descriptions.Item label={t('detail.refEncaissement')}>{att.encaissementRefExterne || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail.compagnie')}>{att.compagnieRaisonSociale || '-'}</Descriptions.Item>
              <Descriptions.Item label={t('detail.typeAttestation')}>
                <Tag>{att.typeAttestation}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.dateEffet')}>
                <strong style={{ color: '#1a6b3c' }}>{att.dateEffet}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.dateEcheance')}>
                <strong style={{ color: '#9b1c1c' }}>{att.dateEcheance}</strong>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.paysEmetteur')}>
                {att.paysEmetteurLibelle} ({att.paysEmetteurCode})
              </Descriptions.Item>
              {att.paysCouvertsCode?.length > 0 && (
                <Descriptions.Item label={t('detail.paysCouverts')}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {att.paysCouvertsCode.map((c) => <Tag key={c}>{c}</Tag>)}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Divider orientation="left" style={{ fontSize: 13 }}>{t('detail.tracabilite')}</Divider>
            <Descriptions size="small" column={1} bordered>
              {digitale && (
                <Descriptions.Item label={t('detail.statutInstance')}>
                  <Tag color={STATUTS_INSTANCE[digitale.statut]?.color || 'default'}>
                    {STATUTS_INSTANCE[digitale.statut]?.label || digitale.statut}
                  </Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('detail.emisLe')}>
                {att.dateEmission ? new Date(att.dateEmission).toLocaleString(localeIntl()) : '-'}
              </Descriptions.Item>
              {digitale && (
                <>
                  <Descriptions.Item label={t('detail.telechargements')}>
                    {digitale.nombreTelechargements} / {digitale.maxTelechargements}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('detail.lienExpireLe')}>
                    {digitale.lienExpireAt ? new Date(digitale.lienExpireAt).toLocaleDateString(localeIntl()) : '-'}
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </div>

          {digitale?.urlVerification && (
            <div className="cb-card" style={{ padding: 20, marginTop: 16, textAlign: 'center' }}>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                {t('detail.qrCode')}
              </p>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(digitale.urlVerification)}`}
                alt={t('detail.qrCodeAlt')}
                style={{ width: 150, height: 150, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                {digitale.urlVerification}
              </p>
            </div>
          )}
        </Col>
      </Row>

      <RenvoyerAttestationModal
        open={renvoyerModal}
        attestation={att}
        onClose={() => setRenvoyerModal(false)}
        onEnvoye={() => { setRenvoyerModal(false); load(); }}
      />
    </Layout>
  );
}
